import useToast from '@hooks/ui/useToast';
import { PaymentErrorType, PrepaidPaymentErrorSnapshot } from '@models/error';
import { CreateOrderRequest } from '@models/order';
import { useFlow } from '@router/stackflow';
import { orderActions } from '@store/order/orderSlice';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';

interface Props {
    type: PaymentErrorType;
    payload: string;
}

function useUserCancel({ type, payload }:Props) {
  const { push, replace } = useFlow();
  const toast = useToast();
  const [cookies, _, deleteCookies] = useCookies(['tempOrder'], { doNotParse: true });
  const dispatch = useDispatch();

  const handleTry = (func: Function) => {
    try {
      func();
    } catch {
      toast.error('데이터를 불러오는 중 오류가 발생하였습니다.');
      replace('MainActivity', {});
    }
  };
  const handleOnPrepayCharge = () => {
    handleTry(() => {
      const data: PrepaidPaymentErrorSnapshot = JSON.parse(payload);
      const { prepaidCardId, amount } = data;
      if (prepaidCardId) {
        toast.info('선불카드 충전을 취소하였습니다.');
        replace('MainActivity', {}, { animate: false });
        push('MyPrepaidCardActivity', {}, { animate: false });
        push('PrepaidCardChargeActivity', { prepaidCardId, amount });
      } else {
        toast.error('선불카드를 찾을 수 없습니다.');
        replace('MainActivity', {});
      }
    });
  };

  const handleOnPrepayPurchase = () => {
    handleTry(() => {
      const data: PrepaidPaymentErrorSnapshot = JSON.parse(payload);
      const { cardImageId, cardName, amount } = data;
      if (cardImageId) {
        toast.info('선불카드 결제를 취소하였습니다.');
        replace('MainActivity', {}, { animate: false });
        push('MyPrepaidCardActivity', {}, { animate: false });
        push('PrepaidCardImagesActivity', { type: 'BUY' }, { animate: false });
        push('PrepaidCardImageActivity', {
          prepaidCardImageId: cardImageId,
          cardName,
          amount,
        });
      } else {
        toast.error('선불카드를 찾을 수 없습니다.');
        replace('MainActivity', {});
      }
    });
  };
  const handleOnPrepayFail = () => {
    toast.error('결제에 실패하였습니다. 잠시 후 다시 시도해주세요.');
    replace('MainActivity', {});
  };

  const handleOnOrderCancel = () => {
    replace('MainActivity', {}, { animate: false });
    try {
      const tempOrder: CreateOrderRequest = JSON.parse(cookies.tempOrder);
      const { shopId } = tempOrder;
      const itemId = tempOrder.orderLines.map((line) => line.shopItemId)
        ?.[0];
      push('ShopListActivity', { type: 'LIST', isOrder: true }, { animate: false });
      push('ItemsActivity', { type: 'ALL', shopId }, { animate: false });
      push('ItemActivity', { itemId, shopId }, { animate: false });
      push('OrderActivity', { }, { animate: false });
      dispatch(orderActions.copy(JSON.parse(cookies.tempOrder!)));
      deleteCookies('tempOrder', { path: '/' });
    } catch (e) {
      console.error(e);
    } finally {
      toast.info('결제가 취소되었습니다.');
    }
  };

  useEffect(() => {
    switch (type) {
      case 'PREPAY_CHARGE':
        handleOnPrepayCharge();
        break;
      case 'PREPAY_PURCHASE':
        handleOnPrepayPurchase();
        break;
      case 'PREPAY_FAIL':
        handleOnPrepayFail();
        break;
      case 'ORDER':
        handleOnOrderCancel();
        break;
      default:
        throw new Error('지원되지 않는 결제 타입입니다.');
    }
  }, [type, payload]);
}

export default useUserCancel;
